import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { MetaData } from 'libs/shared/src/models/structure/pages/metadata';
import { SEO_SERVER_MARKER_NAME } from '../config/seo.constants';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly document = inject(DOCUMENT);
  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);

  /** Sets the meta tags for the current HTML document based on the provided metadata. */
  setMetaTags(metaData: MetaData): void {
    this.applyHeaderHtml(metaData.headerHtml);
    if (metaData.canonicalUrl) this.setCanonicalUrl(metaData.canonicalUrl);
    this.setBreadcrumbListSchema(metaData);
    this.setWebPageSchema(metaData);
  }

  /**
   * Writes the CMS `headerHtml` (meta, link and JSON-LD tags) into the document head once per document and
   * sets {@link SEO_SERVER_MARKER_NAME}. Rendered by the server, so crawlers get the tags without running the
   * bundle; the browser finds the marker and leaves them alone. A `<title>` goes through the Title service and
   * an own description replaces the fallback description of index.html, so neither ends up twice.
   * @param headerHtml Raw HTML string from the CMS metadata field
   */
  writeHeaderHtml(headerHtml: string): void {
    if (this.document.querySelector(`meta[name="${SEO_SERVER_MARKER_NAME}"]`)) return;
    const container = this.document.createElement('div');
    container.innerHTML = headerHtml ?? '';

    // Array.from and appendChild: the server DOM knows neither NodeList.forEach nor append()
    for (const el of Array.from(container.querySelectorAll('title'))) {
      if (el.textContent) this.titleService.setTitle(el.textContent);
      el.remove();
    }
    if (container.querySelector('meta[name="description"]')) this.document.head.querySelector('meta[name="description"]')?.remove();

    const marker = this.document.createElement('meta');
    marker.setAttribute('name', SEO_SERVER_MARKER_NAME);
    marker.setAttribute('content', 'true');
    this.document.head.appendChild(marker);
    for (const node of Array.from(container.childNodes)) this.document.head.appendChild(node);
  }

  /**
   * Marks the current document as non-indexable. Only for a page the backend reports as gone — after a
   * transient load failure the `index,follow` of the shell head has to stay.
   */
  setNoIndex(): void {
    this.metaService.updateTag({ name: 'robots', content: 'noindex' });
  }

  /**
   * Parses the CMS `headerHtml` string and applies contained `<meta>`, `<title>` and
   * canonical `<link>` tags via Angular's Meta/Title services.
   * @param headerHtml Raw HTML string from the CMS metadata field
   */
  private applyHeaderHtml(headerHtml: string): void {
    if (!headerHtml) return;

    const container = this.document.createElement('div');
    container.innerHTML = headerHtml;

    container.querySelectorAll('meta').forEach((el) => {
      const name = el.getAttribute('name');
      const property = el.getAttribute('property');
      const content = el.getAttribute('content') ?? '';
      const httpEquiv = el.getAttribute('http-equiv');

      if (name) {
        this.metaService.updateTag({ name, content });
      } else if (property) {
        this.metaService.updateTag({ property, content });
      } else if (httpEquiv) {
        this.metaService.updateTag({ httpEquiv: httpEquiv, content });
      }
    });

    container.querySelectorAll('title').forEach((el) => {
      if (el.textContent) this.titleService.setTitle(el.textContent);
    });

    container.querySelectorAll('link[rel="canonical"]').forEach((el) => {
      const href = el.getAttribute('href');
      if (href) this.setCanonicalUrl(href);
    });
  }

  /** Sets the breadcrumb list schema for the given metadata and adds it as a JSON-LD script. */
  setBreadcrumbListSchema(metaData: MetaData): void {
    if (!metaData.schemaOrg) return;
    const breadcrumbSchemaData = metaData.schemaOrg.find((schema) => schema['@type'] === 'BreadcrumbList');
    if (!breadcrumbSchemaData) return;
    const breadcrumbList = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbSchemaData.itemListElement,
      itemListOrder: breadcrumbSchemaData.itemListOrder,
      numberOfItems: breadcrumbSchemaData.numberOfItems
    };
    this.upsertJsonLdScript('breadcrumb-list', breadcrumbList);
  }

  /** Sets the schema for a web page using the provided metadata and adds it as a JSON-LD script. */
  setWebPageSchema(metaData: MetaData): void {
    if (!metaData.schemaOrg) return;
    const webPageSchemaData = metaData.schemaOrg.find((schema) => schema['@type'] === 'WebPage');
    if (!webPageSchemaData) return;
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: webPageSchemaData?.name,
      copyrightHolder: webPageSchemaData?.copyrightHolder,
      copyrightYear: webPageSchemaData?.copyrightYear,
      isFamilyFriendly: webPageSchemaData?.isFamilyFriendly
    };
    this.upsertJsonLdScript('webpage', webPageSchema);
  }

  /**
   * Adds or updates a JSON-LD script element in the document head with the given ID and schema data.
   * If a script with the specified ID already exists, it will be updated with the new schema data.
   * Otherwise, a new script element will be created and appended to the document head.
   */
  private upsertJsonLdScript(id: string, schema: object): void {
    const scriptId = `ld-json-${id}`;
    let script = this.document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      this.document.head.appendChild(script);
    }
    script.text = JSON.stringify(schema);
  }

  setCanonicalUrl(url: string): void {
    if (!url || url === 'null' || !/^https?:\/\//i.test(url)) return;
    const link: HTMLLinkElement = this.createOrGetCanonicalElement();
    link.setAttribute('href', url);
  }

  createOrGetCanonicalElement(): HTMLLinkElement {
    let link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    return link;
  }
}
